// Lib
import React, { ReactElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

// Selectors
import { getPlatformDetailsSelector } from '../../../../../../platform/platformSelector';

// Utils
import { isMobileMode, isPlatformIpad } from '../../../../../../platform/utils/platformDetailsUtils';

// Hooks
import { useActiveTiptapEditorCallback } from '../../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Components

// Styles
import './TextStylePopupContent.scss';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';

type TextStylePopupContentProps = {
    showBlockStyleTools: boolean;
    blockStyleComponents: ReactNode;
    textColorComponents: ReactNode;
    textHighlightComponents: ReactNode;
};

const TextStylePopupContent = (props: TextStylePopupContentProps): ReactElement => {
    const { showBlockStyleTools, blockStyleComponents, textColorComponents, textHighlightComponents } = props;
    const translate = useTranslator('popup', 'textStyle');

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isIpad = isPlatformIpad(platformDetails);
    const isMobileAppMode = isMobileMode(platformDetails);

    // This is required for iPad to keep the focus on the editor after the click event
    const keepFocus = useActiveTiptapEditorCallback((activeEditor) => {
        activeEditor?.commands.focus();
    }, []);

    return (
        <div
            className={classNames('TextStylePopupContent', { 'color-only': !showBlockStyleTools })}
            onClick={isIpad ? keepFocus : undefined}
            // In the popup, keep the caret in the editor when clicking popup chrome (titles, gaps, padding);
            // Skipped in mobile, so it cannot interfere with the sheet's drag/scroll gestures.
            onPointerDown={isMobileAppMode ? undefined : (event) => event.preventDefault()}
        >
            {showBlockStyleTools && <div className="block-styles">{blockStyleComponents}</div>}
            <div className="text-color-section color">
                <div className="text-color-section-title">{translate('colorSectionTitle')}</div>
                <div className="text-color-row">{textColorComponents}</div>
            </div>
            <div className="text-color-section background">
                <div className="text-color-section-title">{translate('highlightSectionTitle')}</div>
                <div className="text-color-row">{textHighlightComponents}</div>
            </div>
        </div>
    );
};

export default TextStylePopupContent;
